import React from "react";
import { fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Pencil, Copy, Archive, ArchiveRestore, Globe, Lock } from "lucide-react";

const Pill = ({ children, tone = "muted", testid }) => (
  <span data-testid={testid} className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${tone === "green" ? "bg-emerald-100 text-emerald-800" : tone === "amber" ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground"}`}>{children}</span>
);

// Catalogue card: value-first summary (what you buy / what we throw in / what you pay / what you gain) + admin controls.
export const PlanCard = ({ plan: p, cur, labels, onEdit, onDuplicate, onFlag }) => {
  const n = p.numbers || {};
  const f = (v) => fmtCurrency(v || 0, cur, 0);
  return (
    <Card className={`rounded-2xl p-5 flex flex-col gap-3 ${p.active && !p.archived ? "" : "border-dashed"}`} data-testid={`plan-card-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-lg leading-tight truncate" data-testid={`plan-name-${p.id}`}>{p.name}</h3>
          {p.internal_name && <div className="text-[11px] text-muted-foreground truncate">Internal: {p.internal_name}</div>}
        </div>
        <div className="flex flex-wrap gap-1 justify-end shrink-0">
          {p.archived ? <Pill>Archived</Pill> : p.active ? <Pill tone="green" testid={`plan-status-${p.id}`}>Active</Pill> : <Pill tone="amber" testid={`plan-status-${p.id}`}>Inactive</Pill>}
          {p.staff_only ? <Pill><Lock className="inline h-3 w-3 mr-0.5 -mt-0.5" />Staff only</Pill> : p.online && <Pill><Globe className="inline h-3 w-3 mr-0.5 -mt-0.5" />Online</Pill>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground">{labels.packages_bundle_label}</div>
          {(p.bundle || []).map((b, i) => <div key={i} className="truncate">{b.qty} × {b.service_name}</div>)}
          {(p.bundle || []).length === 0 && <div className="text-muted-foreground">Nothing yet</div>}
          <div className="text-muted-foreground">value {f(n.bundle_value)}</div>
        </div>
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground">{labels.packages_bribes_label}</div>
          {(p.bribes || []).slice(0, 3).map((b) => <div key={b.id} className="truncate">{b.qty > 1 ? `${b.qty} × ` : ""}{b.name}{b.value > 0 ? ` — ${f(b.value * (b.qty || 1))}` : ""}</div>)}
          {(p.bribes || []).length > 3 && <div className="text-muted-foreground">+{p.bribes.length - 3} more</div>}
          {(p.bribes || []).length === 0 && <div className="text-muted-foreground">None</div>}
        </div>
      </div>
      <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">Pay</div>
          <div className="font-heading font-extrabold text-2xl tabular-nums" data-testid={`plan-price-${p.id}`}>{f(n.price)}</div>
          <div className="text-[11px] text-muted-foreground line-through">{f(n.total_value)} total value</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono-label uppercase tracking-wider text-muted-foreground">Client gains</div>
          <div className="font-heading font-bold text-lg text-primary tabular-nums" data-testid={`plan-advantage-${p.id}`}>{f(n.total_advantage)}</div>
          <div className="text-[11px] text-muted-foreground">{n.sessions_total} treatments · {Math.round(n.effective_discount_pct || 0)}% eff.</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/60">
        <div className="flex items-center gap-2 text-xs">
          <Switch checked={!!p.active && !p.archived} disabled={p.archived} onCheckedChange={(v) => onFlag({ active: v }, v ? "Package activated" : "Package deactivated")} data-testid={`plan-active-${p.id}`} />
          <span className="text-muted-foreground">Active</span>
          <Switch checked={!!p.online && !p.staff_only} disabled={p.staff_only || p.archived} onCheckedChange={(v) => onFlag({ online: v }, v ? "Visible online" : "Hidden online")} data-testid={`plan-online-${p.id}`} />
          <span className="text-muted-foreground">Online</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} title="Edit" data-testid={`plan-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDuplicate} title="Duplicate" data-testid={`plan-duplicate-${p.id}`}><Copy className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onFlag({ archived: !p.archived }, p.archived ? "Restored" : "Archived")} title={p.archived ? "Restore" : "Archive"} data-testid={`plan-archive-${p.id}`}>{p.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</Button>
        </div>
      </div>
    </Card>
  );
};
